// src/pages/hr/DepartmentEmployeesPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { employeesService } from '../../services/hrService';
import { Employee } from '../../types/firestore';
import {
  Building,
  Users,
  Search,
  Mail,
  Phone,
  Calendar,
  MapPin,
  UserCheck,
  Filter,
  Eye,
  Edit,
  MoreVertical,
  Award,
  Clock,
  TrendingUp,
  Briefcase,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useCurrency } from '../../contexts/CurrencyContext';

interface DepartmentStats {
  name: string;
  count: number;
  activeCount: number;
  averageSalary: number;
  employees: Employee[];
}

const DepartmentEmployeesPage: React.FC = () => {
  const { formatAmount } = useCurrency();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  // Load employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const allEmployees = await employeesService.getAllEmployees();
        setEmployees(allEmployees);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Group employees by department and calculate stats
  const departmentStats = useMemo(() => {
    const departments: { [key: string]: Employee[] } = {};

    employees.forEach(employee => {
      const dept = employee.department || 'غير محدد';
      if (!departments[dept]) {
        departments[dept] = [];
      }
      departments[dept].push(employee);
    });

    return Object.entries(departments).map(([name, deptEmployees]) => ({
      name,
      count: deptEmployees.length,
      activeCount: deptEmployees.filter(emp => emp.isActive).length,
      averageSalary:
        deptEmployees.reduce((sum, emp) => sum + (emp.salary?.base || 0), 0) /
        deptEmployees.length,
      employees: deptEmployees,
    }));
  }, [employees]);

  // Filter employees based on search and department
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const matchesSearch =
        searchTerm === '' ||
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDepartment =
        selectedDepartment === '' || employee.department === selectedDepartment;

      return matchesSearch && matchesDepartment;
    });
  }, [employees, searchTerm, selectedDepartment]);

  const handleViewEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='large' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
            الموظفين حسب الأقسام
          </h1>
          <p className='text-gray-600 dark:text-gray-400'>
            إدارة وعرض جميع الموظفين مقسمين حسب الأقسام
          </p>
        </div>

        {/* Stats Overview */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
          <div className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <div className='p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg'>
                <Users className='w-6 h-6 text-blue-600 dark:text-blue-400' />
              </div>
              <div className='mr-4'>
                <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                  إجمالي الموظفين
                </p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {employees.length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <div className='p-2 bg-green-100 dark:bg-green-900/30 rounded-lg'>
                <UserCheck className='w-6 h-6 text-green-600 dark:text-green-400' />
              </div>
              <div className='mr-4'>
                <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                  موظفين نشطين
                </p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {employees.filter(emp => emp.isActive).length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <div className='p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg'>
                <Building className='w-6 h-6 text-purple-600 dark:text-purple-400' />
              </div>
              <div className='mr-4'>
                <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                  عدد الأقسام
                </p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {departmentStats.length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <div className='p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg'>
                <TrendingUp className='w-6 h-6 text-yellow-600 dark:text-yellow-400' />
              </div>
              <div className='mr-4'>
                <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                  متوسط الراتب
                </p>
                <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                  {employees.length > 0
                    ? Math.round(
                        employees.reduce(
                          (sum, emp) => sum + (emp.salary?.base || 0),
                          0
                        ) / employees.length
                      ).toLocaleString()
                    : 0}{' '}
                  ج.م
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-8'>
          <div className='flex flex-col lg:flex-row gap-4'>
            <div className='flex-1'>
              <div className='relative'>
                <Search className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                <input
                  type='text'
                  placeholder='البحث عن موظف...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='w-full pr-10 pl-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                />
              </div>
            </div>

            <div className='lg:w-48'>
              <div className='relative'>
                <Filter className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                <select
                  value={selectedDepartment}
                  onChange={e => setSelectedDepartment(e.target.value)}
                  title='تصفية حسب القسم'
                  className='w-full pr-10 pl-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none'
                >
                  <option value=''>جميع الأقسام</option>
                  {departmentStats.map(dept => (
                    <option key={dept.name} value={dept.name}>
                      {dept.name} ({dept.count})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {employees.length === 0 ? (
          <div className='text-center py-12'>
            <Users className='w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
              لا توجد موظفين
            </h3>
            <p className='text-gray-500 dark:text-gray-400'>
              لم يتم إضافة أي موظفين بعد
            </p>
          </div>
        ) : (
          <>
            {/* Department Cards */}
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8'>
              {departmentStats.map(dept => (
                <div
                  key={dept.name}
                  className='bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer'
                  onClick={() =>
                    setSelectedDepartment(
                      selectedDepartment === dept.name ? '' : dept.name
                    )
                  }
                >
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                      {dept.name}
                    </h3>
                    <Building className='w-5 h-5 text-gray-400' />
                  </div>

                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-500 dark:text-gray-400'>
                        إجمالي الموظفين
                      </span>
                      <span className='text-sm font-medium text-gray-900 dark:text-white'>
                        {dept.count}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-500 dark:text-gray-400'>
                        نشطين
                      </span>
                      <span className='text-sm font-medium text-green-600 dark:text-green-400'>
                        {dept.activeCount}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-500 dark:text-gray-400'>
                        متوسط الراتب
                      </span>
                      <span className='text-sm font-medium text-gray-900 dark:text-white'>
                        {formatAmount(Math.round(dept.averageSalary))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Employees List */}
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700'>
              <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  {selectedDepartment
                    ? `موظفي قسم ${selectedDepartment}`
                    : 'جميع الموظفين'}
                  <span className='text-sm text-gray-500 dark:text-gray-400 mr-2'>
                    ({filteredEmployees.length} موظف)
                  </span>
                </h3>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                  <thead className='bg-gray-50 dark:bg-gray-900'>
                    <tr>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        الموظف
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        القسم
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        المنصب
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        الراتب
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        الحالة
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                    {filteredEmployees.map(employee => (
                      <tr
                        key={employee.id}
                        className='hover:bg-gray-50 dark:hover:bg-gray-700'
                      >
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center'>
                            <div className='h-10 w-10 flex-shrink-0'>
                              <div className='h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center'>
                                <span className='text-white font-medium text-sm'>
                                  {employee.name
                                    .split(' ')
                                    .map(n => n[0])
                                    .join('')
                                    .toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className='mr-4'>
                              <div className='text-sm font-medium text-gray-900 dark:text-white'>
                                {employee.name}
                              </div>
                              <div className='text-sm text-gray-500 dark:text-gray-400 flex items-center'>
                                <Mail className='w-3 h-3 ml-1' />
                                {employee.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span className='px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full'>
                            {employee.department}
                          </span>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                          {employee.position}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                          {employee.salary?.base.toLocaleString()}{' '}
                          {employee.salary?.currency}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              employee.isActive
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }`}
                          >
                            {employee.isActive ? 'نشط' : 'غير نشط'}
                          </span>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                          <div className='flex items-center space-x-2 space-x-reverse'>
                            <button
                              onClick={() => handleViewEmployee(employee)}
                              className='text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300'
                              title='عرض تفاصيل الموظف'
                            >
                              <Eye className='w-4 h-4' />
                            </button>
                            <button
                              className='text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                              title='تعديل الموظف'
                            >
                              <Edit className='w-4 h-4' />
                            </button>
                            <button
                              className='text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                              title='خيارات إضافية'
                            >
                              <MoreVertical className='w-4 h-4' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Employee Details Modal */}
        {showEmployeeModal && selectedEmployee && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
            <div className='bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
              <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  تفاصيل الموظف
                </h3>
                <button
                  onClick={() => setShowEmployeeModal(false)}
                  className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                >
                  ✕
                </button>
              </div>

              <div className='p-6 space-y-6'>
                {/* Basic Info */}
                <div>
                  <h4 className='text-md font-medium text-gray-900 dark:text-white mb-3'>
                    المعلومات الأساسية
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        الاسم
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.name}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        رقم الموظف
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.employeeId}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        البريد الإلكتروني
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.email}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        الهاتف
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.phone}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Job Info */}
                <div>
                  <h4 className='text-md font-medium text-gray-900 dark:text-white mb-3'>
                    معلومات الوظيفة
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        القسم
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.department}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        المنصب
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.position}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        الدور
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.role}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        نوع العقد
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.employment.contractType}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Salary Info */}
                <div>
                  <h4 className='text-md font-medium text-gray-900 dark:text-white mb-3'>
                    معلومات الراتب
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        الراتب الأساسي
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.salary?.base.toLocaleString()}{' '}
                        {selectedEmployee.salary?.currency}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        نوع الدفع
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.salary?.payrollType}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Employment Dates */}
                <div>
                  <h4 className='text-md font-medium text-gray-900 dark:text-white mb-3'>
                    تواريخ العمل
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        تاريخ التوظيف
                      </label>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedEmployee.employment.hireDate
                          .toDate()
                          .toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                    <div>
                      <label className='text-sm text-gray-500 dark:text-gray-400'>
                        الحالة
                      </label>
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                          selectedEmployee.isActive
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}
                      >
                        {selectedEmployee.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentEmployeesPage;
